/**
 * Type-to-confirm dialog for deleting a customer.
 *
 * <h3>Why typing, and not just a second click</h3>
 * Deleting a customer takes every vehicle they own and their whole service history with
 * it, and there is no undo. A two-click confirm guards against a mis-tap; it does not
 * guard against deleting the WRONG customer, which is the realistic failure when the
 * list is full of similar names. Typing the name forces the person to look at which row
 * they are actually on.
 *
 * Shared by both tabs — the fleet list and the walk-in list — because a confirmation
 * this consequential must not drift into two versions with different wording or a
 * different match rule.
 *
 * <h3>Matching is deliberately lenient</h3>
 * Trimmed and case-insensitive. The check exists to prove intent, not to test typing
 * accuracy: rejecting "acme motors" for "Acme Motors" teaches people to copy-paste,
 * which defeats the point of reading the name at all.
 */

import React, { useEffect, useState } from 'react';
import { AlertTriangle, Loader2, X } from 'lucide-react';

/** What the customer must be called for the confirmation to be answerable. */
export const confirmNameFor = (customer) =>
    (customer?.fullName || customer?.businessName || customer?.name || '').trim();

const DeleteCustomerDialog = ({ open, customer, onConfirm, onClose, deleting, error }) => {
    const [typed, setTyped] = useState('');

    // Re-seed on open so a cancelled attempt cannot leave a matching value behind and
    // arm the button before the next person has read anything.
    useEffect(() => {
        if (open) setTyped('');
    }, [open, customer?.uuid]);

    if (!open || !customer) return null;

    const expected = confirmNameFor(customer);
    const vehicleCount = customer.vehicleCount ?? customer.vehicles?.length ?? 0;

    /*
     * A customer with no name at all cannot be confirmed by typing one — the phone bot
     * does create these. Falling back to the rego would be worse (it is not shown on
     * this row), so an unnamed customer is confirmed by typing DELETE instead. Stated
     * explicitly in the copy rather than left to be discovered.
     */
    const unnamed = expected === '';
    const target = unnamed ? 'DELETE' : expected;
    const matches = typed.trim().toLowerCase() === target.toLowerCase();

    const submit = (e) => {
        e.preventDefault();
        if (matches && !deleting) onConfirm();
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/50" onClick={deleting ? undefined : onClose} />

            <div className="relative w-full max-w-md rounded-2xl border border-gray-200 bg-white shadow-xl">
                <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4">
                    <div className="flex items-center gap-3">
                        <div className="flex h-9 w-9 items-center justify-center rounded-xl border border-red-100 bg-red-50">
                            <AlertTriangle size={18} className="text-red-600" />
                        </div>
                        <h2 className="font-display text-lg font-semibold text-gray-900">
                            Delete customer
                        </h2>
                    </div>
                    <button type="button" onClick={onClose} disabled={deleting} aria-label="Close"
                            className="rounded-lg p-2 text-gray-400 hover:bg-gray-100 disabled:opacity-50">
                        <X size={18} />
                    </button>
                </div>

                <form onSubmit={submit}>
                    <div className="space-y-4 px-6 py-5">
                        {/* Names the second consequence. Someone clicking Delete on a customer
                            expects the customer to go; they do not expect to lose the vehicles
                            and the service history unless told. */}
                        <p className="text-sm text-gray-600">
                            This permanently deletes{' '}
                            <span className="font-semibold text-gray-900">
                                {unnamed ? 'this unnamed customer' : expected}
                            </span>
                            {vehicleCount > 0 && (
                                <>
                                    , their{' '}
                                    <span className="font-semibold text-gray-900">
                                        {vehicleCount} {vehicleCount === 1 ? 'vehicle' : 'vehicles'}
                                    </span>
                                </>
                            )}
                            {' '}and their service history. This can't be undone.
                        </p>

                        <div>
                            <label htmlFor="delete-confirm" className="mb-1.5 block text-sm font-medium text-gray-700">
                                {unnamed
                                    ? <>Type <span className="font-mono font-semibold">DELETE</span> to confirm</>
                                    : <>Type <span className="font-mono font-semibold">{expected}</span> to confirm</>}
                            </label>
                            <input
                                id="delete-confirm"
                                type="text"
                                value={typed}
                                autoComplete="off"
                                onChange={(e) => setTyped(e.target.value)}
                                placeholder={target}
                                className="w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm focus:border-red-500 focus:ring-2 focus:ring-red-500"
                            />
                        </div>

                        {error && (
                            <div className="flex items-start gap-2 rounded-xl border border-red-100 bg-red-50 p-3">
                                <AlertTriangle size={15} className="mt-0.5 shrink-0 text-red-500" />
                                {/* The server's message names how many invoices, jobs or
                                    bookings are blocking, which is the actionable part. */}
                                <p className="text-sm text-red-600">{error}</p>
                            </div>
                        )}
                    </div>

                    <div className="flex items-center justify-end gap-3 border-t border-gray-100 px-6 py-4">
                        <button type="button" onClick={onClose} disabled={deleting}
                                className="rounded-xl px-4 py-2.5 text-sm font-medium text-gray-600 hover:bg-gray-100 disabled:opacity-50">
                            Cancel
                        </button>
                        <button type="submit" disabled={!matches || deleting}
                                className="inline-flex items-center gap-2 rounded-xl bg-red-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-40">
                            {deleting && <Loader2 size={15} className="animate-spin" />}
                            {deleting ? 'Deleting…' : 'Delete customer'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default DeleteCustomerDialog;
